import { useMemo } from 'react';
import { useLocale } from 'react-aria';
import { useTranslation } from 'react-i18next';
import { type ClientSession } from '@/types/Auth.ts';
import { getSessionTitle } from './sessionDevice';

const COUNTRY_CODE_PATTERN = /^[A-Z]{2}$/;

const RELATIVE_TIME_UNITS: Array<[Intl.RelativeTimeFormatUnit, number]> = [
  ['day', 86_400_000],
  ['hour', 3_600_000],
  ['minute', 60_000],
];

const parseTime = (value: string): number | undefined => {
  const time = Date.parse(value);

  return Number.isNaN(time) ? undefined : time;
};

export function useSessionFormatters() {
  const { t } = useTranslation();
  const { locale } = useLocale();

  return useMemo(() => {
    const regionNames = new Intl.DisplayNames([locale], { type: 'region', fallback: 'none' });
    const dateTimeFormat = new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    const relativeTimeFormat = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });

    return {
      getTitle: (session: ClientSession) => getSessionTitle(session.user_agent) || t('sessions_unknown_device'),

      getCountry: (session: ClientSession) => {
        const code = session.metadata?.country?.toUpperCase() ?? '';

        return { code, name: COUNTRY_CODE_PATTERN.test(code) ? regionNames.of(code) : undefined };
      },

      formatDateTime: (value: string) => {
        const time = parseTime(value);

        return time === undefined ? undefined : dateTimeFormat.format(time);
      },

      formatTimeAgo: (value: string) => {
        const time = parseTime(value);

        if (time === undefined) return undefined;

        const elapsed = Math.max(0, Date.now() - time);
        const match = RELATIVE_TIME_UNITS.find(([, size]) => elapsed >= size);

        return match
          ? relativeTimeFormat.format(-Math.floor(elapsed / match[1]), match[0])
          : relativeTimeFormat.format(0, 'second');
      },
    };
  }, [t, locale]);
}
